import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { supabase } from "../../supabaseClient";
import { claimImportedAthleteData } from "../../utils/claimImportedAthleteData";
import AthleteCalendar from "../../components/AthleteCalendar";
import RightSidebar from "../../components/RightSidebar";
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X, Shield } from "lucide-react";

// --------------------------------------------------
// UTILITY FUNCTION
// --------------------------------------------------
function summarizeDrills(drills) {
    if (!drills || drills.length === 0) return "No drills scheduled.";
    const totalDrills = drills.length;
    const totalDuration = drills.reduce((sum, d) => sum + (parseInt(d.duration_or_reps) || 0), 0);
    return `${totalDrills} drill${totalDrills > 1 ? "s" : ""}, ${totalDuration} min`;
}

function normalizeEmail(value) {
    return String(value || "").trim().toLowerCase();
}

function clampReadinessPercent(userScore, targetScore) {
    const user = Number(userScore);
    const target = Number(targetScore);
    if (!Number.isFinite(user) || !Number.isFinite(target) || target <= 0) return 0;
    return Math.max(0, Math.min((user / target) * 100, 100));
}

export default function NewDashboard({ userId }) {
    // --------------------------------------------------
    // STATE MANAGEMENT
    // --------------------------------------------------
    const [loading, setLoading] = useState(false);
    const [devProgramLoading, setDevProgramLoading] = useState(false);
    const [isProgramHydrating, setIsProgramHydrating] = useState(false);
    const navigate = useNavigate();

    // User & Athlete State
    const [athlete, setAthlete] = useState(null);
    const [programs, setPrograms] = useState([]);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [claimRefreshKey, setClaimRefreshKey] = useState(0);

    // Invite State
    const [pendingInvites, setPendingInvites] = useState([]);
    const [showInviteModal, setShowInviteModal] = useState(false);

    // Position & Sport Logic
    const [detectedSportId, setDetectedSportId] = useState(null);
    const [currentPositionName, setCurrentPositionName] = useState("N/A");
    const [currentPositionId, setCurrentPositionId] = useState(null);
    const [userSportInfo, setUserSportInfo] = useState(null);
    const [playerTypeName, setPlayerTypeName] = useState("N/A");

    // Workout & Score Data
    const [skills, setSkills] = useState([]);
    const [combineScores, setCombineScores] = useState([]);
    const [skillDrills, setSkillDrills] = useState([]);
    const [todaysSCWorkouts, setTodaysSCWorkouts] = useState([]);
    const [hasProgramPaidAccess, setHasProgramPaidAccess] = useState(false);

    // Calculated Scores
    const [skillPercentage, setSkillPercentage] = useState(0);
    const [combinePercentage, setCombinePercentage] = useState(0);
    const [totalProgramScore, setTotalProgramScore] = useState(0);

    // --------------------------------------------------
    // 1. FETCH ATHLETE PROFILE + INVITES (runs independently, no program needed)
    // --------------------------------------------------
    useEffect(() => {
        if (!userId) return;

        let cancelled = false;

        async function loadAthleteAndInvites() {
            try {
                // Fetch athlete row and auth user (for email) in parallel
                const [{ data: athleteData, error: athleteErr }, { data: { user } }] = await Promise.all([
                    supabase.from("users").select("*").eq("id", userId).single(),
                    supabase.auth.getUser()
                ]);

                if (cancelled) return;
                if (athleteErr) throw athleteErr;

                const loginEmail = user?.email || athleteData?.email || "";
                if (loginEmail) {
                    const claimResult = await claimImportedAthleteData({ userId, email: loginEmail });
                    if (!cancelled && claimResult.hasChanges) {
                        setClaimRefreshKey((prev) => prev + 1);
                    }
                }

                setAthlete(athleteData);

                // Fetch invites using the auth email
                if (user?.email) {
                    const { data: invites } = await supabase
                        .from("player_invites")
                        .select(`*, programs (name), teams (name)`)
                        .eq("email", user.email)
                        .eq("status", "pending");

                    if (!cancelled) {
                        const list = invites || [];
                        setPendingInvites(list);
                        // Auto-open modal if there are pending invites
                        if (list.length > 0) setShowInviteModal(true);
                    }
                }
            } catch (err) {
                console.error("[NewDashboard] Athlete/Invite load error:", err);
                // Unblock the UI even on error
                if (!cancelled) setAthlete({ full_name: "Athlete", email: "" });
            }
        }

        loadAthleteAndInvites();
        return () => { cancelled = true; };
    }, [userId]);

    // --------------------------------------------------
    // 2. FETCH PROGRAMS
    // --------------------------------------------------
    useEffect(() => {
        if (!userId) return;

        let cancelled = false;

        async function fetchPrograms() {
            try {
                const { data, error } = await supabase
                    .from("user_program_follows")
                    .select(`program_id, programs(name, player_peaking, sport, off_season_start_month, off_season_end_month), order_index`)
                    .eq("user_id", userId)
                    .order('order_index', { ascending: true }); // Lower numbers (1) at the top        ]);
                    ;

                if (cancelled) return;
                if (error) throw error;

                const list = data || [];
                setPrograms(list);

                // Default to first program if available
                if (list.length > 0) {
                    const p = list[0];
                    setSelectedProgram({
                        id: p.program_id,
                        name: p.programs.name,
                        player_peaking: p.programs.player_peaking,
                        sport: p.programs.sport,
                        off_season_start_month: p.programs.off_season_start_month,
                        off_season_end_month: p.programs.off_season_end_month,
                    });
                }
            } catch (err) {
                console.error("[NewDashboard] Program fetch error:", err);
            }
        }

        fetchPrograms();
        return () => { cancelled = true; };
    }, [userId, claimRefreshKey]);

    // --------------------------------------------------
    // 3. PROGRAM-SPECIFIC DATA (Sport, Position, Scores, Workouts)
    //    Only runs once a program with a sport is selected.
    // --------------------------------------------------
    useEffect(() => {
        if (!userId || !selectedProgram || !selectedProgram.sport) return;

        let cancelled = false;

        async function fetchProgramData() {
            setIsProgramHydrating(true);
            try {
                const today = dayjs().format("YYYY-MM-DD");

                // --- A. Identify Sport ID ---
                const { data: sportLookup, error: sportError } = await supabase
                    .from("development_sports")
                    .select("id")
                    .eq("name", selectedProgram.sport)
                    .single();

                if (cancelled) return;
                if (sportError || !sportLookup) {
                    console.error("[NewDashboard] Sport lookup failed for:", selectedProgram.sport);
                    return;
                }
                const sportId = sportLookup.id;
                setDetectedSportId(sportId);

                // --- B. Fetch Sport Info, Scores, and Workouts in parallel ---
                const [
                    { data: sportInfoData },
                    { data: swotRows },
                    { data: skillData },
                    { data: combineData },
                    { data: workoutSummaryData },
                    { data: scData }
                ] = await Promise.all([
                    supabase
                        .from("user_sport_info")
                        .select("modeled_player")
                        .eq("user_id", userId)
                        .eq("sport", sportId)
                        .maybeSingle(),
                    supabase
                        .from("athlete_swot")
                        .select("position_id, player_type, updated_at")
                        .eq("athlete_id", userId)
                        .eq("program_id", selectedProgram.id)
                        .not("position_id", "is", null)
                        .is("secondary_position_id", null)
                        .order("updated_at", { ascending: false })
                        .limit(1),
                    supabase.from("user_skill_scores")
                        .select(`id, user_score, program_goal_skill_scores!fk_user_skill_scores_program_goal_skill_score (skill_name, target_score)`)
                        .eq("athlete_id", userId)
                        .eq("program_id", selectedProgram.id),
                    supabase.from("user_combine_score")
                        .select(`id, user_score, program_combine_scores (drill_name, target_score)`)
                        .eq("athlete_id", userId)
                        .eq("program_id", selectedProgram.id),
                    supabase.from("skill_workouts")
                        .select("id")
                        .eq("athlete_id", userId)
                        .eq("program_id", selectedProgram.id)
                        .eq("workout_date", today),
                    supabase.from("daily_exercise_drills")
                        .select("*")
                        .eq("user_id", userId)
                        .eq("followed_program", selectedProgram.id)
                        .eq("drill_date", today)
                        .order("order_index", { ascending: true }),
                ]);

                if (cancelled) return;

                setUserSportInfo(sportInfoData);

                // --- C. Resolve Position & Player Type names ---
                let resolvedPosName = "N/A";
                let resolvedPosId = null;
                let resolvedPlayerTypeName = "N/A";
                const swotRow = (swotRows || [])[0] || null;
                const resolvedPlayerTypeId = swotRow?.player_type || null;
                resolvedPosId = swotRow?.position_id || null;

                const [posRefRes, ptRefRes] = await Promise.all([
                    resolvedPosId
                        ? supabase.from("positions").select("name").eq("id", resolvedPosId).maybeSingle()
                        : Promise.resolve({ data: null }),
                    resolvedPlayerTypeId
                        ? supabase.from("player_types").select("name").eq("id", resolvedPlayerTypeId).maybeSingle()
                        : Promise.resolve({ data: null })
                ]);

                if (posRefRes?.data?.name) resolvedPosName = posRefRes.data.name;
                if (ptRefRes?.data?.name) resolvedPlayerTypeName = ptRefRes.data.name;

                if (cancelled) return;

                setCurrentPositionName(resolvedPosName);
                setCurrentPositionId(resolvedPosId);
                setPlayerTypeName(resolvedPlayerTypeName);

                // --- D. Fetch workout drills if any exist today ---
                let drillsData = [];
                const workoutIds = (workoutSummaryData || []).map(w => w.id);
                if (workoutIds.length > 0) {
                    const { data: drills } = await supabase
                        .from("workout_drills")
                        .select("*")
                        .in("workout_id", workoutIds)
                        .order('block_order')
                        .order('order_index');
                    drillsData = drills || [];
                }

                if (cancelled) return;

                // --- E. Set all data & calculate scores ---
                setSkills(skillData || []);
                setCombineScores(combineData || []);
                setSkillDrills(drillsData);
                setTodaysSCWorkouts(scData || []);

                const skillAvg = (skillData || []).length
                    ? (skillData || []).reduce((sum, s) => (
                        sum + clampReadinessPercent(s.user_score, s.program_goal_skill_scores?.target_score)
                    ), 0) / skillData.length
                    : 0;
                const combineAvg = (combineData || []).length
                    ? (combineData || []).reduce((sum, c) => (
                        sum + clampReadinessPercent(c.user_score, c.program_combine_scores?.target_score)
                    ), 0) / combineData.length
                    : 0;

                setSkillPercentage(Math.round(skillAvg));
                setCombinePercentage(Math.round(combineAvg));
                setTotalProgramScore(Math.round((skillAvg + combineAvg) / 2));

            } catch (err) {
                console.error("[NewDashboard] Program data fetch error:", err);
            } finally {
                if (!cancelled) setIsProgramHydrating(false);
            }
        }

        fetchProgramData();
        return () => { cancelled = true; };
    }, [userId, selectedProgram]);

    useEffect(() => {
        const fetchProgramPurchaseAccess = async () => {
            if (!userId || !selectedProgram?.id) {
                setHasProgramPaidAccess(false);
                return;
            }

            try {
                const today = dayjs().format("YYYY-MM-DD");
                const [{ data: rosterRows, error: rosterError }, { data: purchaseRows, error: purchaseError }] = await Promise.all([
                    supabase
                        .from("rostered_users")
                        .select("id, teams!inner(program_id)")
                        .eq("user_id", userId)
                        .eq("teams.program_id", selectedProgram.id)
                        .limit(1),
                    supabase
                        .from("program_purchases")
                        .select("id")
                        .eq("program_id", selectedProgram.id)
                        .eq("status", "active")
                        .lte("start_date", today)
                        .gte("end_date", today)
                        .limit(1),
                ]);

                if (rosterError) throw rosterError;
                if (purchaseError) throw purchaseError;

                const isRostered = (rosterRows || []).length > 0;
                const hasActivePurchase = (purchaseRows || []).length > 0;
                setHasProgramPaidAccess(isRostered && hasActivePurchase);
            } catch (err) {
                console.error("Program purchase access error:", err);
                setHasProgramPaidAccess(false);
            }
        };

        fetchProgramPurchaseAccess();
    }, [userId, selectedProgram?.id]);

    // --------------------------------------------------
    // 4. HANDLERS
    // --------------------------------------------------
    const handleInviteResponse = async (invite, action) => {
        try {
            const status = action === 'accept' ? 'accepted' : 'rejected';
            await supabase.from("player_invites").update({ status }).eq("id", invite.id);

            if (action === 'accept') {
                await claimImportedAthleteData({ userId, email: invite.email });

                if (invite.team_id) {
                    const { data: existingRosterRow, error: existingRosterError } = await supabase
                        .from("rostered_users")
                        .select("id")
                        .eq("team_id", invite.team_id)
                        .eq("user_id", userId)
                        .maybeSingle();
                    if (existingRosterError) throw existingRosterError;

                    if (!existingRosterRow?.id) {
                        const { error: insertRosterError } = await supabase
                            .from("rostered_users")
                            .insert([{ user_id: userId, team_id: invite.team_id, role: "Athlete" }]);
                        if (insertRosterError) throw insertRosterError;
                    }
                }
                const { data: existingFollow, error: existingFollowError } = await supabase
                    .from("user_program_follows")
                    .select("id")
                    .eq("user_id", userId)
                    .eq("program_id", invite.program_id)
                    .maybeSingle();
                if (existingFollowError) throw existingFollowError;
                if (!existingFollow?.id) {
                    const { error: followInsertError } = await supabase
                        .from("user_program_follows")
                        .insert([{ user_id: userId, program_id: invite.program_id }]);
                    if (followInsertError) throw followInsertError;
                }

                const normalizedInviteEmail = normalizeEmail(invite.email);
                if (normalizedInviteEmail) {
                    const { data: existingPublished, error: existingPublishedError } = await supabase
                        .from("athlete_swot")
                        .select("id")
                        .eq("program_id", invite.program_id)
                        .eq("athlete_id", userId)
                        .eq("status", "published")
                        .maybeSingle();
                    if (existingPublishedError) throw existingPublishedError;

                    if (!existingPublished) {
                        const { data: prebuiltRows, error: prebuiltError } = await supabase
                            .from("athlete_swot")
                            .select("id, prebuilt_email, created_at")
                            .eq("program_id", invite.program_id)
                            .is("athlete_id", null)
                            .in("status", ["draft", "published"])
                            .order("created_at", { ascending: false });
                        if (prebuiltError) throw prebuiltError;

                        const matchedPrebuilt = (prebuiltRows || []).find(
                            (row) => normalizeEmail(row.prebuilt_email) === normalizedInviteEmail
                        );

                        if (matchedPrebuilt?.id) {
                            const { error: assignSwotError } = await supabase
                                .from("athlete_swot")
                                .update({
                                    athlete_id: userId,
                                    status: "published",
                                    prebuilt_name: null,
                                    prebuilt_email: null,
                                    updated_at: new Date().toISOString()
                                })
                                .eq("id", matchedPrebuilt.id)
                                .is("athlete_id", null);
                            if (assignSwotError) throw assignSwotError;
                        }
                    }
                }

                alert(`Welcome to ${invite.programs?.name}!`);
                window.location.reload();
            }
            setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
            if (pendingInvites.length <= 1) setShowInviteModal(false);
        } catch (err) {
            console.error("Invite Error:", err);
            alert("Error processing invite.");
        }
    };

    const handleProfileImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const filePath = `profiles/${userId}/${file.name}`;
            const { error } = await supabase.storage.from("profile_images").upload(filePath, file, { upsert: true });
            if (error) throw error;
            const { data: publicUrl } = supabase.storage.from("profile_images").getPublicUrl(filePath);
            await supabase.from("users").update({ profile_image_url: publicUrl.publicUrl }).eq("id", userId);
            setAthlete(prev => ({ ...prev, profile_image_url: publicUrl.publicUrl }));
        } catch (err) {
            console.error("Image upload failed:", err.message);
        }
    };

    const monthLabel = (value) => {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const index = Number(value) - 1;
        return months[index] || "N/A";
    };

    const offseasonWindowLabel = () => {
        const startMonth = Number(selectedProgram?.off_season_start_month);
        const endMonth = Number(selectedProgram?.off_season_end_month);
        if (!startMonth || !endMonth) return "offseason window";
        return `${monthLabel(startMonth)} - ${monthLabel(endMonth)}`;
    };

    const handleDevProgramCheckout = () => {
        if (!selectedProgram?.id) return;
        navigate(`/athlete/offseason-program?programId=${selectedProgram.id}`);
    };

    // --------------------------------------------------
    // RENDER GUARDS
    // --------------------------------------------------
    const isProfileLoading = !athlete;
    const displayAthlete = athlete || { full_name: "Athlete" };

    // --------------------------------------------------
    // RENDER
    // --------------------------------------------------
    return (
        <div className="p-4 sm:p-6 md:p-8 bg-gray-50 min-h-screen text-gray-900 space-y-6">

            {!hasProgramPaidAccess && (
                <div className="flex justify-end w-full">
                    <button
                        onClick={handleDevProgramCheckout}
                        disabled={devProgramLoading || programs.length === 0}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-xl shadow transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {devProgramLoading ? "Starting checkout..." : "Buy Offseason Program"}
                    </button>
                </div>
            )}

            {/* ---------------- Athlete Info Header ---------------- */}
            <div className="bg-white rounded-2xl shadow-md border p-6 flex items-center gap-6">
                <div className="relative w-24 h-24 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 shadow-sm group">
                    {displayAthlete.profile_image_url ? (
                        <img src={displayAthlete.profile_image_url} alt={displayAthlete.full_name} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xl font-semibold">
                            {displayAthlete.full_name ? displayAthlete.full_name.charAt(0) : "U"}
                        </div>
                    )}
                    <label htmlFor="profile-upload" className="absolute inset-0 bg-black bg-opacity-50 text-white text-xs font-semibold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        Change Photo
                    </label>
                    <input id="profile-upload" type="file" accept="image/*" className="hidden" onChange={handleProfileImageUpload} />
                </div>

                <div className="flex flex-col gap-1">
                    <h2 className={`text-2xl font-extrabold text-gray-900 ${isProfileLoading ? "animate-pulse bg-gray-200 text-transparent rounded" : ""}`}>
                        {displayAthlete.full_name || "New Athlete"}
                    </h2>
                    <div className="text-gray-600 text-sm flex flex-wrap gap-4">
                        {/* Only render these if the values exist — avoids "Height: null'null"" for new users */}
                        {displayAthlete.height_feet && <span>Height: {displayAthlete.height_feet}'{displayAthlete.height_inches}"</span>}
                        {displayAthlete.weight && <span>Weight: {displayAthlete.weight} lbs</span>}
                        {programs.length > 0 && (
                            <>
                                <span className={`${currentPositionId ? "text-gray-600" : "text-gray-400"} ${isProgramHydrating ? "animate-pulse bg-gray-200 text-transparent rounded" : ""}`}>
                                    Position: {currentPositionName}
                                </span>
                                <span className={`${isProgramHydrating ? "animate-pulse bg-gray-200 text-transparent rounded" : ""}`}>
                                    Player Type: {playerTypeName}
                                </span>
                                <span className={`${isProgramHydrating ? "animate-pulse bg-gray-200 text-transparent rounded" : ""}`}>
                                    Model Athlete: {userSportInfo?.modeled_player || "N/A"}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ---------------- Program Selector ---------------- */}
            <div className="flex overflow-x-auto gap-3 py-2 mb-4 scrollbar-hide">
                {programs.length === 0 && (
                    <span className="text-sm text-gray-500 italic py-2">
                        No programs joined yet. Check your invites or browse available programs.
                    </span>
                )}

                {/* Sort by order_index: lower numbers (1) come before higher numbers (2) */}
                {[...programs]
                    .sort((a, b) => (a.programs.order_index || 0) - (b.programs.order_index || 0))
                    .map(p => (
                        <button
                            key={p.program_id}
                            onClick={() => setSelectedProgram({
                                id: p.program_id,
                                name: p.programs.name,
                                player_peaking: p.programs.player_peaking,
                                sport: p.programs.sport,
                                off_season_start_month: p.programs.off_season_start_month,
                                off_season_end_month: p.programs.off_season_end_month,
                            })}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold shadow flex-shrink-0 transition ${selectedProgram?.id === p.program_id
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-200 hover:bg-gray-300"
                                }`}
                        >
                            {p.programs.name}
                        </button>
                    ))
                }
            </div>

            {/* ---------------- Zero State: No Programs ---------------- */}
            {programs.length === 0 && (
                <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-300">
                    <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl">
                        🚀
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                        Welcome to NuvioSport, {displayAthlete.full_name?.split(" ")[0] || "Athlete"}!
                    </h3>
                    <p className="text-gray-500 max-w-md mx-auto mt-2 mb-8">
                        You don't have any active programs yet. Join a team or browse available programs to start your journey.
                    </p>
                    <button
                        onClick={() => navigate("/marketplace")}
                        className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg transition-transform active:scale-95"
                    >
                        Browse Programs
                    </button>
                </div>
            )}

            {/* ---------------- Main Layout (only when a program is active) ---------------- */}
            {programs.length > 0 && selectedProgram && (
                <div className="flex gap-6 flex-col lg:flex-row">
                    {isProgramHydrating ? (
                        <>
                            <div className="flex-1">
                                <div className="h-[520px] rounded-2xl bg-white border animate-pulse" />
                            </div>
                            <div className="w-full lg:w-[360px]">
                                <div className="h-[520px] rounded-2xl bg-white border animate-pulse" />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex-1">
                                <AthleteCalendar
                                    userId={userId}
                                    programId={selectedProgram?.id}
                                    onEventSelect={(ev) => console.log("Event selected:", ev)}
                                    onEventsLoaded={(events) => console.log("Events loaded:", events.length)}
                                />
                            </div>

                            <div className="w-full lg:w-[360px]">
                                <RightSidebar
                                    skills={skills}
                                    combineScores={combineScores}
                                    todaysSCWorkouts={todaysSCWorkouts}
                                    workouts={skillDrills}
                                    totalProgramScore={totalProgramScore}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ---------------- INVITE MODAL ---------------- */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900">
                                <Shield className="text-blue-600 w-6 h-6" /> Team Invites
                            </h2>
                            <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-gray-600 p-1">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {pendingInvites.map(invite => (
                                <div key={invite.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50 hover:bg-white hover:shadow-md transition">
                                    <div className="mb-4">
                                        <h3 className="font-bold text-lg text-gray-900">{invite.programs?.name}</h3>
                                        <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                                            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded">Team</span>
                                            {invite.teams?.name || "Full Program"}
                                        </p>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => handleInviteResponse(invite, 'accept')}
                                            className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg text-sm font-semibold transition"
                                        >
                                            <Check size={18} /> Join Team
                                        </button>
                                        <button
                                            onClick={() => handleInviteResponse(invite, 'decline')}
                                            className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 py-2.5 rounded-lg text-sm font-semibold transition"
                                        >
                                            <X size={18} /> Decline
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
